import React, { useCallback, useEffect, useState } from "react";
import { Mail, RefreshCw } from "lucide-react";
import { api } from "../../lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { TemplateReview } from "../../components/ops/TemplateReview";
import { GoLiveStatus, UnsubscribeReasons } from "../../components/ops/EmailInsights";

const STATUS_STYLES = {
  delivered: "bg-emerald-100 text-emerald-800",
  held: "bg-slate-200 text-slate-700",
  pending: "bg-amber-100 text-amber-800",
  processing: "bg-sky-100 text-sky-800",
  failed: "bg-rose-100 text-rose-800",
  cancelled: "bg-slate-100 text-slate-500",
};

const fmt = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

const Badge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[status] || "bg-slate-100"}`}>{status}</span>
);

const Table = ({ tid, head, rows }) => (
  <div className="overflow-x-auto border rounded bg-white">
    <table className="w-full text-xs" data-testid={tid}>
      <thead className="bg-slate-50 text-slate-600">
        <tr>{head.map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

export default function OpsEmail() {
  const [summary, setSummary] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [audit, setAudit] = useState([]);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const q = status ? `&status=${status}` : "";
      const [s, o, a] = await Promise.all([
        api.get("/btc_email/summary"),
        api.get(`/btc_email/outbox?limit=100${q}`),
        api.get("/btc_email/audit?limit=100"),
      ]);
      setSummary(s.data); setOutbox(o.data.items); setAudit(a.data.items);
    } catch {
      setErr("Email delivery state is available to administrators only.");
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="ops-email-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5" /> Email delivery</h1>
        <button onClick={load} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border rounded hover:bg-slate-50" data-testid="ops-email-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <Tabs defaultValue="delivery">
        <TabsList>
          <TabsTrigger value="delivery" data-testid="ops-email-tab-delivery">Delivery</TabsTrigger>
          <TabsTrigger value="templates" data-testid="ops-email-tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="templates"><TemplateReview /></TabsContent>
        <TabsContent value="delivery" className="space-y-6">
      <p className="text-sm text-slate-600 max-w-3xl">
        Read-only delivery metadata. Email bodies, links, tokens and recipient addresses are never shown here.
      </p>
      {err && <div className="text-sm text-rose-700" data-testid="ops-email-error">{err}</div>}
      {summary && <GoLiveStatus summary={summary} />}
      {summary && (
        <div className="flex flex-wrap gap-3 text-sm" data-testid="ops-email-summary">
          <span className="px-3 py-1 rounded bg-white border" data-testid="ops-email-environment">Environment: {summary.environment}</span>
          <span className="px-3 py-1 rounded bg-white border" data-testid="ops-email-kill-switch">Sending: {summary.sending_enabled ? "on" : "off"}</span>
          {Object.entries(summary.counts).map(([k, v]) => (
            <button key={k} onClick={() => setStatus(status === k ? "" : k)} data-testid={`ops-email-filter-${k}`}
              className={`px-3 py-1 rounded border ${status === k ? "bg-slate-900 text-white" : "bg-white"}`}>{k}: {v}</button>
          ))}
        </div>
      )}
      {summary && <UnsubscribeReasons />}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Outbox</h2>
        <Table tid="ops-email-outbox-table"
          head={["Event", "Status", "Recipient ref", "Queued", "Attempts", "Last attempt", "Delivered", "Provider ref", "Reason"]}
          rows={outbox.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`ops-email-outbox-row-${r.id}`}>
              <td className="px-3 py-2 font-mono">{r.event}</td><td className="px-3 py-2"><Badge status={r.status} /></td>
              <td className="px-3 py-2 font-mono">{r.recipient_ref}</td><td className="px-3 py-2">{fmt(r.queued_at)}</td>
              <td className="px-3 py-2">{r.attempt_count}</td><td className="px-3 py-2">{fmt(r.last_attempt_at)}</td>
              <td className="px-3 py-2">{fmt(r.delivered_at)}</td><td className="px-3 py-2 font-mono">{r.provider_message_id || "—"}</td>
              <td className="px-3 py-2">{r.failure_category || r.firewall_decision || "—"}</td>
            </tr>
          ))} />
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Delivery audit</h2>
        <Table tid="ops-email-audit-table"
          head={["Time", "Event", "Status", "Attempts", "Decision", "Failure", "Provider ref"]}
          rows={audit.map((r, i) => (
            <tr key={`${r.outbox_id}-${i}`} className="border-t">
              <td className="px-3 py-2">{fmt(r.timestamp)}</td><td className="px-3 py-2 font-mono">{r.event}</td>
              <td className="px-3 py-2"><Badge status={r.status} /></td><td className="px-3 py-2">{r.attempt_count}</td>
              <td className="px-3 py-2">{r.firewall_decision || "—"}</td><td className="px-3 py-2">{r.failure_category || "—"}</td>
              <td className="px-3 py-2 font-mono">{r.provider_message_id || "—"}</td>
            </tr>
          ))} />
      </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
